<template>
  <el-input
    ref="input"
    v-model="val"
    class="sui-input"
    :class="`sui-sticky-field--${elMode}`"
    :type="type"
    :style="{
      width: elWidth
    }"
    :size="elSize"
    :disabled="elDisabled"
    :maxlength="_maxlength"
    :minlength="minlength"
    :placeholder="_placeholder"
    :clearable="clearable"
    :show-password="showPassword"
    :prefix-icon="prefixIcon"
    :suffix-icon="suffixIcon"
    :readonly="readonly"
    :tabindex="tabindex"
    :validate-event="validateEvent"
    :show-word-limit="showWordLimit"
    :rows="rows"
    :autosize="autosize"
    :autocomplete="autocomplete"
    :name="name"
    :max="max"
    :min="min"
    :autofocus="autofocus"
    :label="label"
    :show-tooltip="showTooltip"
    :tooltip-max-width="tooltipMaxWidth"
    :chchar-unit="_chcharUnit"
    :auto-trim="autoTrim"
    v-bind="$attrs"
    @blur="handleBlur"
    @focus="handleFocus"
    @clear="handleClear"
    @keyup.native="keyup"
    @keydown.native="handleKeyDown"
  >
    <slot v-if="showPrepend" slot="prepend" name="prepend">
      <sui-prop
        v-if="prepend && prepend.mold"
        :tag="'sui-' + prepend.mold"
        :prop="prepend"
        @input="prepend.input"
        @click="prepend.click"
        @change="prepend.change"
      ></sui-prop>
      <template v-else-if="prepend">{{ prepend.text }}</template>
    </slot>
    <slot v-if="showAppend" slot="append" name="append">
      <sui-prop
        v-if="append && append.mold"
        :tag="'sui-' + append.mold"
        :prop="append"
        @input="append.input"
        @click="append.click"
        @change="append.change"
      ></sui-prop>
      <template v-else-if="append">{{ append.text }}</template>
    </slot>
    <slot slot="prefix" name="prefix"></slot>
    <slot slot="suffix" name="suffix"></slot>
  </el-input>
</template>
<script>
import formMixin from './formMixin'
import { debounce } from 'throttle-debounce'
export default {
  name: 'SuiInput',
  mixins: [formMixin],
  props: {
    type: {
      type: String,
      default: 'text'
    },
    maxlength: {
      type: Number,
      default: null
    },
    chcharUnit: {
      // 中文代表几个字节长度
      type: Number,
      default: 1
    },
    maxcharlength: {
      type: Number,
      default: null
    },
    minlength: {
      type: Number,
      default: null
    },
    rows: {
      type: Number,
      default: null
    },
    autosize: {
      type: [Boolean, Object],
      default: false
    },
    autocomplete: {
      type: String, // on | off
      default: 'off'
    },
    name: {
      type: String,
      default: null
    },
    max: {
      type: Number,
      default: null
    },
    min: {
      default: null,
      type: Number
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: null
    },
    clearable: {
      type: Boolean,
      default: true
    },
    showPassword: {
      type: Boolean,
      default: null
    },
    prefixIcon: {
      type: String,
      default: null
    },
    suffixIcon: {
      type: String,
      default: null
    },
    readonly: {
      type: Boolean,
      default: null
    },
    tabindex: {
      type: String,
      default: null
    },
    validateEvent: {
      type: Boolean,
      default: true
    },
    showWordLimit: {
      type: Boolean,
      default: null
    },
    prepend: {
      type: Object,
      default: null
    },
    append: {
      type: Object,
      default: null
    },
    keyup: {
      type: Function,
      default: () => {}
    },
    keydown: {
      type: Function,
      default: () => {}
    },
    // 2021-1-19 新增input值过长时，显示气泡
    showTooltip: {
      type: Boolean,
      default: false
    },
    tooltipMaxWidth: {
      type: String,
      default: '100%'
    },
    autoTrim: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      funcArr: ['change', 'click', 'input']
    }
  },
  computed: {
    // 是否开启字节长度限制
    isCharLengthControl() {
      return this._maxlength && this._chcharUnit > 1
    },
    val: {
      get() {
        if (this.prop && this.SuiForm) {
          return this.SuiForm.model[this.prop]
        } else {
          return this.value
        }
      },
      set(val) {
        this.setLimitLenInput(val)
      }
    },
    showPrepend() {
      return this.prepend || this.$scopedSlots.prepend
    },
    showAppend() {
      return this.append || this.$scopedSlots.append
    },
    _placeholder() {
      if (this.placeholder !== '' && this.placeholder !== null) {
        return this._locale(this.placeholder)
      } else if (this.placeholder === '') {
        return ''
      } else {
        return undefined
      }
    },
    _chcharUnit() {
      return this.maxlength === null && this.maxcharlength !== null ? 2 : isNaN(this.chcharUnit) ? 1 : Math.floor(this.chcharUnit)
    },
    _maxlength() {
      return this.maxcharlength !== null ? this.maxcharlength : this.maxlength
    }
  },
  created() {
    this.repairPrepend()
    this.repairAppend()
  },
  methods: {
    // 计算字节长度：中文按chcharUnit个字节计算，英文数字按1个字节计算
    countByteLen(str) {
      let len = 0
      if (str) {
        // 防止赋值为null报错
        for (let i = 0; i < str.length; i++) {
          if (/^[\u4e00-\u9fa5]+$/.test(str.charAt(i))) {
            len += this._chcharUnit
          } else {
            len++
          }
        }
      }
      return len
    },
    // 如果开启字节限制，则通过该方法对val进行字节长度判断处理
    setLimitLenInput(value) {
      if (!this.isCharLengthControl) {
        if (this.prop && this.SuiForm) {
          this.SuiForm.model[this.prop] = value
        } else {
          this.$emit('input', value)
        }
        return
      }
      const dom = this.$refs.input.getInput()
      // 将输入的内容用数组记录
      const inputValueArr = []
      let inputValueArrIndex = 0
      for (let i = 0; i < value.length; i++) {
        const char = value.charAt(i)
        // 判断是否为中文
        const isChineseChar = /^[\u4e00-\u9fa5]+$/.test(char)
        if (isChineseChar) {
          for (let i = 1; i < this._chcharUnit; i++) {
            // 通过空字符串来占位，模拟中文占两个字节长度
            inputValueArr[inputValueArrIndex++] = ''
          }
        }
        inputValueArr[inputValueArrIndex++] = char
      }
      // 截取用户输入前upperLimit 个字符数
      if (this.val && this._maxlength <= this.countByteLen(value)) {
        const _value = value.length < this.val.length || this.countByteLen(value) === this._maxlength ? value : this.val
        if (this.prop && this.SuiForm) {
          this.SuiForm.model[this.prop] = _value
        } else {
          this.$emit('input', _value)
        }
        const selectionStart = dom.selectionStart
        // 纠正光标位置x
        setTimeout(() => {
          dom.focus()
          dom.setSelectionRange(selectionStart, selectionStart)
        })
        return
      }
      // 未达到最大限制则更新val的值
      if (this._maxlength >= this.countByteLen(this.val)) {
        const result = inputValueArr.slice(0, this._maxlength).join('')
        if (this.prop && this.SuiForm) {
          this.SuiForm.model[this.prop] = result
        } else {
          this.$emit('input', result)
        }
        return
      }
      if (this._maxlength > this.countByteLen(value)) {
        if (this.prop && this.SuiForm) {
          this.SuiForm.model[this.prop] = value
        } else {
          this.$emit('input', value)
        }
      }
    },
    repairPrepend() {
      if (this.prepend && this.prepend.mold) {
        if (this.prepend.value && typeof this.prepend.input !== 'function') {
          this.prepend.input = (val) => {
            this.prepend.value = val
          }
        }
        this.funcArr.forEach((func) => {
          if (typeof this.prepend[func] !== 'function') {
            this.prepend[func] = () => {}
          }
        })
      }
    },
    repairAppend() {
      if (this.append && this.append.mold) {
        if (this.append.value && typeof this.append.input !== 'function') {
          this.append.input = (val) => {
            this.append.value = val
          }
        }
        this.funcArr.forEach((func) => {
          if (typeof this.append[func] !== 'function') {
            this.append[func] = () => {}
          }
        })
      }
    },
    focus() {
      this.$refs.input.focus()
    },
    blur() {
      this.$refs.input.blur()
    },
    // 搜索触发节流，避免高频异常操作
    handleSearch: debounce(500, true, function () {
      if (this.SuiFormPage && this.SuiFormPage.enableEnterSearch) {
        this.SuiFormPage.$refs.form.valid((data) => {
          this.SuiFormPage.$emit('query', data)
        })
      }
    }),
    handleKeyDown(e) {
      typeof this.keydown === 'function' && this.keydown(e)
      if (e.keyCode === 13) {
        this.handleSearch()
      }
    }
    // select () {
    //   this.$refs.input.select();
    // },
  }
}
</script>
<style lang="scss" scoped></style>



// WEBPACK FOOTER //
// src/components/SimbartUI/components/form/SuiInput.vue